import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './styles/app.css'
import { App } from './App'
import { ErrorBoundary } from './components/ErrorBoundary'

/**
 * `/dev` renders the F0 design harness, but only in development.
 *
 * `import.meta.env.DEV` is replaced with a literal at build time, so this branch
 * — and the dynamic import inside it — is dead code in production and gets
 * dropped entirely. That is deliberately stronger than a route guard: on the
 * live site the harness is not merely unreachable, it is not in the bundle.
 *
 * It had been publicly reachable at /dev, where a debug page showing worker
 * timings under an "F0 foundation" heading reads as an unfinished product to
 * anyone who wanders in from a launch thread.
 *
 * No router. This is a single-screen app whose only other route is a debug page
 * that gets deleted, and a routing library would cost more bundle than the chart.
 */
const devPath = import.meta.env.DEV ? window.location.pathname.replace(/\/+$/, '') : ''

const container = document.getElementById('root')
if (container === null) throw new Error('#root missing from index.html')

const root = createRoot(container)

if (devPath === '/dev') {
  void import('./dev/DevHarness').then(({ DevHarness }) => {
    root.render(
      <StrictMode>
        <DevHarness />
      </StrictMode>,
    )
  })
} else if (devPath === '/dev/card') {
  // Where the share card gets designed, and where public/og.png is generated.
  void import('./dev/CardLab').then(({ CardLab }) => {
    root.render(
      <StrictMode>
        <CardLab />
      </StrictMode>,
    )
  })
} else {
  root.render(
    <StrictMode>
      {/*
        Wraps the product, not a subtree. A throw anywhere in App used to unmount
        the whole tree and leave a blank page with nothing to report; this keeps
        the error on screen where it can be screenshotted.
      */}
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  )
}
